import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useShop } from '../context/ShopContext';
import { useCart } from '../context/CartContext';
import { useFavorites } from '../hooks/useFavorites';
import FavoriteProductCard from '../components/FavoriteProductCard';
import Header from '../components/Header';
import Footer from '../components/Footer';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Δημόσια σελίδα αγαπημένων ανά eshop (localStorage ανά merchant / owner_id).
 * Έτσι κάθε κατάστημα έχει ορατή επιλογή χωρίς να απαιτείται σύνδεση για προβολή λίστας.
 */
export default function FavoritesPage() {
    const { shopName, merchantId } = useShop();
    const { addToCart } = useCart();
    const { favoriteIds, removeFavorite } = useFavorites(merchantId);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!merchantId || !favoriteIds.length) {
                setProducts([]);
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                const results = await Promise.all(
                    favoriteIds.map(async (id) => {
                        try {
                            const { data } = await axios.get(`${API_URL}/products/${id}`);
                            return data;
                        } catch {
                            return null;
                        }
                    })
                );
                if (!cancelled) setProducts(results.filter(Boolean));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [merchantId, favoriteIds]);

    const handleAddToCart = async (product) => {
        await addToCart(product, 1);
    };

    const handleRemoveFavorite = (productId) => {
        removeFavorite(productId);
        toast.success('Αφαιρέθηκε από τα αγαπημένα');
    };

    return (
        <div className="min-h-screen bg-background flex flex-col">
            <Header />

            <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-8">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center text-red-600 border border-red-100">
                        <Heart className="w-6 h-6 fill-current" />
                    </div>
                    <div>
                        <h1 className="font-display text-2xl font-semibold text-primary">Αγαπημένα</h1>
                        <p className="text-sm text-muted-foreground">
                            Αποθηκευμένα προϊόντα μόνο για το <span className="font-medium text-foreground">{shopName}</span>
                        </p>
                    </div>
                </div>

                {!merchantId ? (
                    <p className="text-muted-foreground text-center py-12">Φόρτωση καταστήματος...</p>
                ) : loading ? (
                    <p className="text-center py-12 text-muted-foreground">Φόρτωση...</p>
                ) : products.length === 0 ? (
                    <div className="text-center py-12 bg-muted/30 rounded-2xl">
                        <Heart className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground mb-2">Δεν έχετε αγαπημένα σε αυτό το κατάστημα.</p>
                        <p className="text-sm text-muted-foreground mb-6">
                            Πατήστε την καρδιά σε προϊόντα στο κατάστημα για να εμφανίζονται εδώ.
                        </p>
                        <Link to="/">
                            <Button className="rounded-full">Περιήγηση προϊόντων</Button>
                        </Link>
                    </div>
                ) : (
                    <div className="space-y-3" data-testid="favorites-page-list">
                        {products.map((p) => (
                            <FavoriteProductCard
                                key={p.id}
                                product={p}
                                onAddToCart={handleAddToCart}
                                onRemoveFavorite={handleRemoveFavorite}
                            />
                        ))}
                    </div>
                )}
            </main>
            <Footer shopName={shopName} />
        </div>
    );
}
